import FormInputComponent from "@via/components/inputs/form-input-component";
import Modal from "@via/components/modal";
import { requiredValidation } from "@via/utils/form-validators";
import { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCreateVmetaField,
  useVmetaCollections,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import { VmetaFieldType } from "common";
import { SelectOption } from "@via/utils/interfaces";
import { useDebounceCallback } from "usehooks-ts";
import SelectComponent from "@via/components/select/select";

interface CreateReferenceFieldModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  collectionId: string;
  projectId: string;
}

const pageSize = 10;

const CreateReferenceFieldModal: FC<CreateReferenceFieldModalProps> = ({
  open,
  setOpen,
  collectionId,
  projectId,
}) => {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [cumulativeCollections, setCumulativeCollections] = useState<
    SelectOption[]
  >([]);

  const { data: collections, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      page,
      pageSize,
      [],
      [
        {
          id: "projectID",
          value: projectId,
        },
        {
          id: "name",
          value: search,
        },
      ]
    );

  const { mutateAsync: createField, isPending: isCreateFieldLoading } =
    useCreateVmetaField();

  useEffect(() => {
    if (!collections) return;

    setCumulativeCollections(prevCollections => {
      const newData = collections.data ?? [];

      if (!newData.length) return [];

      // Append new results while preventing duplicates
      const existingLabels = new Set(prevCollections.map(c => c.value));
      const newCollections = newData
        .map(({ name, label }) => ({
          label: label || name,
          value: name,
        }))
        .filter(c => !existingLabels.has(c.value));

      return [...prevCollections, ...newCollections];
    });
  }, [collections?.data, page, search]);

  const fetchMoreCollections = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((collections?.results ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [collections?.results]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === search) return;
    setPage(0);
    setSearch(value);
    setCumulativeCollections([]);
  }, 300);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      selectedCollectionName: "",
      label: "",
    },
  });

  // Modal closes on true and stays open on false (invalid form) or a rejection (failed create,
  // which the hook reports).
  const handleConfirmModal = async (): Promise<boolean> => {
    let created = false;
    await handleSubmit(async payload => {
      await createField({
        label: payload.label,
        name: `${payload.selectedCollectionName}_id`,
        type: VmetaFieldType.ObjectId,
        collectionID: collectionId,
        ref: `${projectId}_${payload.selectedCollectionName}`,
      });
      created = true;
    })();
    return created;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create Reference Field"
      isConfirmLoading={isCreateFieldLoading}
      onConfirm={handleConfirmModal}>
      <form onSubmit={e => e.preventDefault()}>
        <div className="space-y-6 pb-5 pt-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Please choose a collection to be used for referencing.
          </label>
          <SelectComponent
            control={control}
            name="selectedCollectionName"
            options={cumulativeCollections}
            isLoading={isCollectionsLoading}
            placeholder="Select Collection"
            rules={requiredValidation("Collection")}
            onFetchMoreOptions={fetchMoreCollections}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
            enableClear={false}
          />
          {errors.selectedCollectionName && (
            <span className="text-xs text-rose-500">
              {errors.selectedCollectionName.message}
            </span>
          )}
          <FormInputComponent
            control={control}
            name="label"
            label="Label"
            placeholder="Enter Label"
            showRequiredField
            validations={requiredValidation("Label")}
          />
        </div>
      </form>
    </Modal>
  );
};

export default CreateReferenceFieldModal;
